import { useState } from "react";
import { Link } from "react-router-dom";

import { useCollections } from "../data/collections";
import { youtubeTimeUrl } from "../lib/citation";
import { collectionToMarkdown, collectionToText } from "../lib/collections";
import { formatTimestamp } from "../lib/transcript";

import { CopyButton } from "./CopyButton";

/** A persistent tray of gathered quotes, with Markdown and plain-text export. */
export function CollectionsTray() {
  const { quotes, remove, clear } = useCollections();
  const [open, setOpen] = useState(false);
  const count = quotes.length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full border border-hairline bg-warm-white px-4 py-2.5 text-sm font-medium text-warm-brown shadow-sm transition-colors hover:border-gold/50"
        aria-expanded={open}
      >
        Collected
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-terra px-1.5 text-xs text-warm-white">
          {count}
        </span>
      </button>

      {open ? (
        <div className="fixed inset-0 z-50 flex justify-end">
          <button
            type="button"
            aria-label="Close tray"
            className="flex-1 bg-warm-brown/20"
            onClick={() => setOpen(false)}
          />
          <aside className="flex h-full w-full max-w-md flex-col bg-warm-white shadow-xl">
            <header className="flex items-center justify-between border-b border-hairline px-5 py-4">
              <div>
                <p className="eyebrow">Quote sheet</p>
                <h2 className="font-display text-xl text-warm-brown">
                  Collected quotes
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-sm text-warm-gray hover:text-terra"
              >
                Close
              </button>
            </header>

            {count === 0 ? (
              <div className="flex flex-1 items-center justify-center px-8 text-center text-sm text-warm-gray">
                Collect a line from any transcript and it will gather here,
                ready to export for a speech, a deck or a film.
              </div>
            ) : (
              <ul className="flex-1 divide-y divide-hairline overflow-y-auto">
                {quotes.map((quote) => (
                  <li key={`${quote.videoId}#${quote.t}`} className="px-5 py-4">
                    <p className="text-sm leading-relaxed text-warm-brown">
                      {quote.en}
                    </p>
                    {quote.orig.trim().length > 0 ? (
                      <p className="mt-1 font-devanagari text-sm text-warm-gray">
                        {quote.orig}
                      </p>
                    ) : null}
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-warm-gray">
                      <Link
                        to={`/video/${quote.videoId}`}
                        className="truncate hover:text-terra"
                        onClick={() => setOpen(false)}
                        title={quote.title}
                      >
                        {quote.speaker} · {formatTimestamp(quote.t)}
                      </Link>
                      <div className="flex shrink-0 items-center gap-2">
                        <a
                          href={youtubeTimeUrl(quote.url, quote.t)}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:text-terra"
                        >
                          Watch
                        </a>
                        <button
                          type="button"
                          onClick={() => remove(quote.videoId, quote.t)}
                          className="hover:text-terra"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {count > 0 ? (
              <footer className="flex items-center gap-2 border-t border-hairline px-5 py-4">
                <CopyButton
                  text={collectionToMarkdown(quotes)}
                  label="Copy Markdown"
                  copiedLabel="Copied Markdown"
                  className="rounded-full bg-terra px-4 py-2 text-sm font-medium text-warm-white hover:bg-gold"
                />
                <CopyButton
                  text={collectionToText(quotes)}
                  label="Copy text"
                  copiedLabel="Copied text"
                  className="rounded-full border border-hairline px-4 py-2 text-sm font-medium text-warm-brown hover:border-gold/50"
                />
                <button
                  type="button"
                  onClick={clear}
                  className="ml-auto text-sm text-warm-gray hover:text-terra"
                >
                  Clear
                </button>
              </footer>
            ) : null}
          </aside>
        </div>
      ) : null}
    </>
  );
}
